import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BusinessActions from "../../Redux/actions/business-actions";
import PaySwitchActions from "../../Redux/transition/payswitch/payswitch.reducer";
import DocumentActions from "../../Redux/transition/document-upload/document-upload.reducer";
import { getPaymentProviderUiStatus, hasOwnProviderCredentials } from "../../utils/paymentProviderStatus";

const PROVIDERS_REQUIRING_VERIFIED = new Set(["JUNIPAY", "ALPAY", "APPSNMOBILE"]);

export function usePaymentProviderScreen(provider) {
    const dispatch = useDispatch();
    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const onboardingRequestInProgress = useSelector((state) => state.beginOnboarding.onboardingRequestInProgress);
    const failureOnboarding = useSelector((state) => state.beginOnboarding.errorOnboardingRequest);
    const verifStatus = useSelector((state) => state.documents.verifStatus);
    const paymentCredentials = useSelector((state) => state.payswitch.credentials);
    const business = useSelector((state) => state.businesses.business);
    const fetchingVerifStatus = useSelector((state) => state.documents.fetchingVerifStatus);
    const fetchingPaymentCredentials = useSelector((state) => state.payswitch.fetchingCredentials);
    const fetchingBusiness = useSelector((state) => state.businesses.fetchingOne);

    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [hasRequested, setHasRequested] = useState(false);
    const [isOnboardingInProgress, setIsOnboardingInProgress] = useState(false);
    const [activationUnlocked, setActivationUnlocked] = useState(false);

    const businessId = defMembership?.business?.id;
    const requireVerified = PROVIDERS_REQUIRING_VERIFIED.has(provider);
    const ownCredentials = hasOwnProviderCredentials(paymentCredentials, provider) ? paymentCredentials : null;
    const providerStatus = getPaymentProviderUiStatus(provider, {
        business,
        paymentCredentials,
        verifStatus,
        requireVerified,
        activationUnlocked,
    });

    const refreshProviderState = useCallback(() => {
        if (!businessId) {
            return;
        }
        dispatch(BusinessActions.businessRequest(businessId));
        dispatch(PaySwitchActions.paySwitchCredentialRequest(businessId, provider));
        dispatch(DocumentActions.businessVerificationStatusRequest(businessId));
        dispatch(DocumentActions.merchantAgreementsRequest(businessId, provider));
        setHasRequested(true);
    }, [businessId, dispatch, provider]);

    useEffect(() => {
        refreshProviderState();
    }, [refreshProviderState]);

    useEffect(() => {
        if (!hasRequested || initialLoadDone) {
            return;
        }
        if (!fetchingPaymentCredentials && !fetchingVerifStatus && fetchingBusiness !== true) {
            setInitialLoadDone(true);
        }
    }, [businessId, fetchingBusiness, fetchingPaymentCredentials, fetchingVerifStatus, hasRequested, initialLoadDone]);

    useEffect(() => {
        if (!isOnboardingInProgress || onboardingRequestInProgress || failureOnboarding) {
            return;
        }
        setActivationUnlocked(true);
        refreshProviderState();
    }, [failureOnboarding, isOnboardingInProgress, onboardingRequestInProgress, refreshProviderState]);

    return {
        defMembership,
        business,
        businessId: business?.id || businessId,
        verifStatus,
        paymentCredentials: ownCredentials,
        onboardingRequestInProgress,
        failureOnboarding,
        isOnboardingInProgress,
        setIsOnboardingInProgress,
        activationUnlocked,
        providerStatus,
        isScreenLoading: !initialLoadDone,
        refreshProviderState,
    };
}
